import { withoutBlankFilterChoices } from "@/components/non_registrant/dashboard-column-filter-match";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { RegistrantDetailsSheet } from "@/components/non_registrant/registrant-details-sheet";
import { Button } from "@/components/ui/button";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM } from "@/constants";
import type { UserType } from "@/constants";
import { getNonRegistrantRegistrantAccountsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type { NonRegistrantRegistrantAccountListItemSchema } from "@/hera-api/client/types.gen";
import { organizationRoleLabel } from "@/lib/registrant-display";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";

const REGISTRANT_ACCESSOR_KEYS = [
  "id",
  "email",
  "name",
  "company_name",
  "organization_role",
  "is_deleted",
] as const;

type RegistrantAccessorKeys = (typeof REGISTRANT_ACCESSOR_KEYS)[number];
type RegistrantColumnProperties = ColumnProperties<
  NonRegistrantRegistrantAccountListItemSchema,
  RegistrantAccessorKeys
>;

const REGISTRANT_ACCOUNT_COLUMNS_WITHOUT_BLANK_FILTER = new Set<string>([
  "id",
  "email",
  "organization_role",
  "is_deleted",
]);

const buildRegistrantColumnProperties = (
  accessorKey: RegistrantAccessorKeys,
): RegistrantColumnProperties => {
  switch (accessorKey) {
    case "id":
      return {
        displayName: "ID",
        defaultVisible: false,
        getValue: (row) => row.id,
      };
    case "email":
      return {
        displayName: "Email",
        defaultVisible: true,
        getValue: (row) => row.email,
      };
    case "name":
      return {
        displayName: "Name",
        defaultVisible: true,
        getValue: (row) => row.name ?? "",
      };
    case "company_name":
      return {
        displayName: "Company",
        defaultVisible: true,
        getValue: (row) => row.company_name ?? "",
      };
    case "organization_role":
      return {
        displayName: "Organization role",
        defaultVisible: true,
        getValue: (row) => organizationRoleLabel(row),
      };
    case "is_deleted":
      return {
        displayName: "Active",
        defaultVisible: true,
        getValue: (row) => (!row.is_deleted ? "Yes" : "No"),
      };
    default: {
      const _exhaust: never = accessorKey;
      void _exhaust;
      return {
        displayName: String(accessorKey),
        defaultVisible: false,
        getValue: () => "",
      };
    }
  }
};

const getRegistrantColumnProperties = (
  accessorKey: RegistrantAccessorKeys,
): RegistrantColumnProperties =>
  withoutBlankFilterChoices(
    accessorKey,
    REGISTRANT_ACCOUNT_COLUMNS_WITHOUT_BLANK_FILTER,
    buildRegistrantColumnProperties(accessorKey),
  );

interface DashboardRegistrantsProps {
  isAuthSetupComplete: boolean;
  /** When provided, sheet state is controlled by parent (e.g. setup page). Parent should render RegistrantDetailsSheet. */
  selectedRegistrantId?: string | null;
  onRegistrantSelect?: (id: string) => void;
  isRegistrantSheetOpen?: boolean;
  onRegistrantSheetOpenChange?: (open: boolean) => void;
  onOpenOrganization?: (organizationId: string) => void;
  /** Operator portal: show Actions column with registrant portal impersonation link. */
  showImpersonateButton?: boolean;
  /** When set, registrant detail sheet shows accounts-contact toggles for staff. */
  staffPortal?: UserType.OPERATOR | UserType.GOVERNMENT;
  /** Page chrome rendered inside the list scroller so it scrolls with the rows. */
  leadingContent?: ReactNode;
}

export function DashboardRegistrants({
  isAuthSetupComplete,
  selectedRegistrantId: controlledRegistrantId,
  onRegistrantSelect,
  isRegistrantSheetOpen: controlledSheetOpen,
  onRegistrantSheetOpenChange,
  onOpenOrganization: _onOpenOrganization,
  showImpersonateButton = false,
  staffPortal,
  leadingContent,
}: DashboardRegistrantsProps) {
  const [internalRegistrantId, setInternalRegistrantId] = useState<
    string | null
  >(null);
  const [internalSheetOpen, setInternalSheetOpen] = useState(false);

  const isControlled =
    controlledRegistrantId !== undefined &&
    onRegistrantSelect !== undefined &&
    controlledSheetOpen !== undefined &&
    onRegistrantSheetOpenChange !== undefined;

  const selectedRegistrantId = isControlled
    ? (controlledRegistrantId ?? null)
    : internalRegistrantId;
  const isSheetOpen = isControlled ? controlledSheetOpen : internalSheetOpen;
  const setSelectedRegistrantId = isControlled
    ? (id: string | null) => id && onRegistrantSelect?.(id)
    : setInternalRegistrantId;
  const setIsSheetOpen = isControlled
    ? (onRegistrantSheetOpenChange ?? (() => {}))
    : setInternalSheetOpen;

  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({
    is_deleted: ["Yes"], // "Yes" = active (not deleted); default to showing all active registrants
  });

  const {
    data: registrantsData,
    isLoading,
    error,
  } = useQuery({
    ...getNonRegistrantRegistrantAccountsOptions({
      query: { include_deleted: true },
    }),
    enabled: isAuthSetupComplete,
  });

  const handleRowClick = useCallback(
    (registrantId: string) => {
      setSelectedRegistrantId(registrantId);
      setIsSheetOpen(true);
    },
    [setSelectedRegistrantId, setIsSheetOpen],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        {leadingContent}
        <div className="flex justify-center items-center py-8">
          <LoadingScreen message="Fetching registrants..." />
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        {leadingContent}
        <ErrorComponent
          title={getQueryErrorHeading(error)}
          message={getQueryErrorMessage(error)}
        />
      </div>
    );
  }

  const registrants = registrantsData?.registrant_accounts ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0 overflow-hidden">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DashboardTable<
          NonRegistrantRegistrantAccountListItemSchema,
          RegistrantAccessorKeys
        >
          leadingContent={
            leadingContent ? (
              <div className="pb-4">{leadingContent}</div>
            ) : undefined
          }
          lockToViewport
          data={registrants}
          onRowClick={handleRowClick}
          accessorKeys={REGISTRANT_ACCESSOR_KEYS}
          getColumnProperties={getRegistrantColumnProperties}
          getRowId={(row) => row.id}
          tablePersistenceKey="non-registrant-registrants"
          initialSortColumn="email"
          initialSortDirection="asc"
          columnFilters={columnFilters}
          onColumnFiltersChange={setColumnFilters}
          renderRowActions={
            showImpersonateButton
              ? (row) => (
                  <Button
                    size="xs"
                    variant="outline"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(
                        `/registrant/dashboard?${REGISTRANT_PORTAL_IMPERSONATE_URL_PARAM}=${row.id}`,
                        "_blank",
                      );
                    }}
                  >
                    <ExternalLink className="h-3 w-3 mr-1.5" />
                    Impersonate
                  </Button>
                )
              : undefined
          }
        />
      </div>

      {!isControlled && (
        <RegistrantDetailsSheet
          isOpen={isSheetOpen}
          onOpenChange={setIsSheetOpen}
          registrantAccountId={selectedRegistrantId}
          key={selectedRegistrantId}
          showImpersonateButton={showImpersonateButton}
          staffPortal={staffPortal}
        />
      )}
    </div>
  );
}
